import { Formik } from "formik";
import * as React from "react";
import { toast } from "react-toastify";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import {
  FormRow,
  Label,
  Switch
} from "src/library/components/Forms";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import DeliveryDaysPicker from "src/core/shared/components/DeliveryDaysPicker";
import {
  deliveryRoutesApi,
  UpdateDeliveryRoute
} from "src/core/shared/api/deliveryRoutes.api";
import { useProduct } from "src/core/products/hooks";
import { PageProps } from "src/pages/Router/routes";
import { IDeliveryRoute } from "src/core/drops/api/drops.api";

interface UrlParams {
  productId: string;
}

/**
 * Product delivery days page
 *
 */
const ProductDeliveryRoute: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { match, terminology } = props;
  const formRef = React.useRef<Formik<IDeliveryRoute>>(null);
  const productId = match ? parseInt(match.params.productId, 0) : 0;

  const {
    isLoading: isLoadingProduct, product, reloadProduct
  } = useProduct(productId);

  const [ createDeliveryRouteResponse, createDeliveryRoute ] = deliveryRoutesApi<IDeliveryRoute>("CREATE_DELIVERY_ROUTE");
  const [ deleteDeliveryRouteResponse, deleteDeliveryRoute ] = deliveryRoutesApi<IDeliveryRoute>("DELETE_DELIVERY_ROUTE");
  const [ updateDeliveryRouteResponse, updateDeliveryRoute ] = deliveryRoutesApi<UpdateDeliveryRoute>("UPDATE_DELIVERY_ROUTE");
  const [ getDeliveryRouteResponse, getDeliveryRoute ] = deliveryRoutesApi<IDeliveryRoute>("GET_DELIVERY_ROUTE");
  const [ inheritedDeliveryRoute, setInheritedDeliveryRoute ] = React.useState<IDeliveryRoute | null>(null);
  const [ overrideDeliveryDays, setOverrideDeliveryDays ] = React.useState(false);

  React.useEffect(() => {
    getDeliveryRoute(1);
  }, []);

  React.useEffect(() => {
    if (product && product.deliveryRoute && product.deliveryRoute.length) {
      setOverrideDeliveryDays(true);
    } else {
      setOverrideDeliveryDays(false);
    }
  }, [ product ]);

  React.useEffect(() => {
    if (getDeliveryRouteResponse.data) {
      setInheritedDeliveryRoute(getDeliveryRouteResponse.data);
    }
  }, [ getDeliveryRouteResponse ]);

  React.useEffect(() => {
    if (createDeliveryRouteResponse.data) {
      toast.success(`${terminology.product.singular.text} saved!`);
      reloadProduct();

      if (formRef.current) {
        formRef.current.setValues(createDeliveryRouteResponse.data);
      }
    }

    if (createDeliveryRouteResponse.error) {
      toast.error("Could not create delivery route.");
    }
  }, [ createDeliveryRouteResponse ]);

  React.useEffect(() => {
    if (deleteDeliveryRouteResponse.data) {
      reloadProduct();
      toast.success(`${terminology.product.singular.text} saved!`);
    }

    if (deleteDeliveryRouteResponse.error) {
      toast.error(`Could not save delivery route: ${deleteDeliveryRouteResponse.error.message}`);
    }
  }, [ deleteDeliveryRouteResponse.error, deleteDeliveryRouteResponse.data ]);

  React.useEffect(() => {
    if (updateDeliveryRouteResponse.data) {
      reloadProduct();
      toast.success(`${terminology.product.singular.text} saved!`);
    }

    if (updateDeliveryRouteResponse.error) {
      toast.error(`Could not update ${terminology.product.singular.text}.`);
    }
  }, [ updateDeliveryRouteResponse ]);

  const handleSubmitSettings = (form: IDeliveryRoute) => {
    const newFormValues: Partial<IDeliveryRoute> = form;

    // Add product name to form values for delivery route
    newFormValues.name = product ? product.description : "";

    if (!overrideDeliveryDays) {
      if (newFormValues.type === "product" && newFormValues.id) {
        deleteDeliveryRoute<number>(newFormValues.id);
      } else {
        toast.success(`${terminology.product.singular.text} saved!`);
      }
    } else if (newFormValues.id === 0) {
      delete newFormValues.id;
      createDeliveryRoute(newFormValues);
    } else {
      updateDeliveryRoute(newFormValues);
    }
  };

  return (
    <Layout
      {...props}
      title={`${product ? product.description + " - " : ""} Delivery days`}
    >
      <MainContentWrapper fill>
        <Grid.Bounds direction="vertical">
          {isLoadingProduct || getDeliveryRouteResponse.pending ? (
            <LoadingSpinner
              type="circular"
              position="inline"
            />
          ) : (
            product && (
              <Formik
                ref={formRef}
                initialValues={(product.deliveryRoute && product.deliveryRoute[ 0 ] ?
                  product.deliveryRoute[ 0 ] :
                  {
                    id: 0,
                    name: product.description,
                    type: "product",
                    deliveryDays: {},
                    entityId: product.id
                  })}
                onSubmit={handleSubmitSettings}
                render={({
                  handleSubmit, values, setFieldValue
                }) => {
                  return (
                    <form onSubmit={handleSubmit}>
                      <FormRow>
                        <Label
                          for="override-dd"
                          label="Set allowed delivery days"
                        />

                        <Switch
                          name="override-dd"
                          onChange={() => setOverrideDeliveryDays(!overrideDeliveryDays)}
                          checked={overrideDeliveryDays}
                        />
                      </FormRow>

                      {overrideDeliveryDays && (
                        <FormRow>
                          <Label
                            label="Delivery Days:"
                            for="deliveryDays"
                          />

                          <DeliveryDaysPicker
                            deliveryRoutes={[]}
                            value={values}
                            inherited={inheritedDeliveryRoute}
                            isGlobal={false}
                            type="product"
                            handleChange={deliveryRoute => {
                              if (deliveryRoute) {
                                setFieldValue("deliveryDays", deliveryRoute.deliveryDays);
                              }
                            }}
                          />
                        </FormRow>
                      )}

                      <FormRow>
                        <Button
                          type="submit"
                          loading={updateDeliveryRouteResponse.pending}
                        >
                          Save
                        </Button>
                      </FormRow>
                    </form>
                  );
                }}
              />
            )
          )}
        </Grid.Bounds>
      </MainContentWrapper>
    </Layout>
  );
};

export default ProductDeliveryRoute;
